import { DEFAULT_SPEAKER_SETTINGS, SPEECH_PITCH_RANGE, SPEECH_RATE_RANGE } from "./speech-model.js";
//#region src/speech/speech-utils.ts
/**
* Pure helpers of the speech module — no browser access, unit-tested.
*/
var normalizeLang = (lang) => lang.toLowerCase().replace("_", "-");
/**
* Voices whose language matches the locale ("de" matches "de-DE", "de_AT",
* "de"), local engines first, then the platform default, then by name —
* the order an app can show in a picker as-is.
*/
function filterVoicesByLocale(voices, locale) {
	const wanted = normalizeLang(locale);
	return voices.filter((voice) => {
		const lang = normalizeLang(voice.lang);
		return lang === wanted || lang.startsWith(`${wanted}-`);
	}).sort((a, b) => Number(b.localService) - Number(a.localService) || Number(b.default) - Number(a.default) || a.name.localeCompare(b.name));
}
/**
* The voice to use: the configured one if it still exists, otherwise the
* best voice for the locale, otherwise nothing (the engine picks by `lang`).
*/
function pickVoice(voices, locale, voiceURI) {
	if (voiceURI) {
		const configured = voices.find((voice) => voice.voiceURI === voiceURI);
		if (configured) return configured;
	}
	return filterVoicesByLocale(voices, locale)[0];
}
/**
* Split text into chunks of at most `maxLength` characters, preferring
* sentence ends, then commas/semicolons, then spaces. Chrome silently
* stops utterances that run longer than roughly 15 seconds.
*/
function splitIntoChunks(text, maxLength) {
	const trimmed = text.trim();
	if (!trimmed) return [];
	if (trimmed.length <= maxLength) return [trimmed];
	const chunks = [];
	let rest = trimmed;
	while (rest.length > maxLength) {
		const window = rest.slice(0, maxLength + 1);
		const cut = lastBreak(window, /[.!?]\s/g) ?? lastBreak(window, /[,;:]\s/g) ?? lastBreak(window, /\s/g) ?? maxLength;
		chunks.push(rest.slice(0, cut).trim());
		rest = rest.slice(cut).trim();
	}
	if (rest) chunks.push(rest);
	return chunks;
}
/** Index right after the last match of `pattern` in `text`, if any. */
function lastBreak(text, pattern) {
	let end;
	for (const match of text.matchAll(pattern)) end = match.index + match[0].length;
	return end;
}
var clamp = (value, [min, max]) => Math.min(max, Math.max(min, value));
/** Complete settings within the supported ranges (NaN/undefined → default). */
function normalizeSpeakerSettings(settings = {}) {
	const num = (value, fallback) => typeof value === "number" && Number.isFinite(value) ? value : fallback;
	return {
		...settings.voiceURI && { voiceURI: settings.voiceURI },
		rate: clamp(num(settings.rate, DEFAULT_SPEAKER_SETTINGS.rate), SPEECH_RATE_RANGE),
		pitch: clamp(num(settings.pitch, DEFAULT_SPEAKER_SETTINGS.pitch), SPEECH_PITCH_RANGE),
		volume: clamp(num(settings.volume, DEFAULT_SPEAKER_SETTINGS.volume), [0, 1])
	};
}
//#endregion
export { filterVoicesByLocale, normalizeSpeakerSettings, pickVoice, splitIntoChunks };
